/**
 * Fetches display attachments for a set of entity ids from the dedicated
 * public attachments endpoint, keyed by entity id → collection.
 *
 * List endpoints no longer embed attachments — callers fetch the entities
 * first, then call this with the ids to hydrate their images. A failed fetch
 * resolves to `{}` so the page still renders (images fall back to placeholders
 * via `attachmentUrl()`), never throwing.
 *
 *   const byId = await fetchAttachments("maker", ids, ["logo", "profile"]);
 *   maker.attachments = byId[maker.id]; // { logo: [...], profile: [...] }
 */
export type AttachmentMap = Record<string, Record<string, unknown[]>>;

// The backend rejects any request with more than 200 ids (attachmentController
// `MAX_IDS`). That cap is a deliberate DoS/heap guardrail — see
// crashalitics/2026-05-12-live-heap-oom.md — so for large catalogues (a maker
// with 600+ products) we split into ≤200-id requests client-side rather than
// ask the server to relax it. Keep this ≤ the backend MAX_IDS.
const CHUNK_SIZE = 200;

export function useAttachments() {
  const config = useRuntimeConfig();

  async function fetchAttachments(
    modelName: string,
    ids: (string | number)[],
    collections: string[],
    fields?: string[],
  ): Promise<AttachmentMap> {
    const uniqueIds = [...new Set(ids)].filter((id) => id !== undefined && id !== null);
    if (uniqueIds.length === 0 || collections.length === 0) return {};

    const query = `collections=${encodeURIComponent(JSON.stringify(collections))}`;

    const chunks: (string | number)[][] = [];
    for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
      chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
    }

    // One request per ≤200-id chunk, in parallel. The chunks are a disjoint
    // partition of the unique ids, so merging the per-chunk maps is a plain
    // Object.assign with no key collisions. Each chunk falls back to `{}` on
    // failure, so a single failed batch only drops its own images instead of
    // wiping every image on the page.
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        try {
          const response = await $fetch<{ data: AttachmentMap }>(
            `${config.public.apiBase}/api/public/attachments/${modelName}?${query}`,
            {
              method: "POST",
              body: { ids: chunk, ...(fields ? { fields } : {}) },
            },
          );
          return response?.data ?? {};
        } catch {
          return {} as AttachmentMap;
        }
      }),
    );

    return Object.assign({}, ...results);
  }

  return { fetchAttachments };
}
